import { Component, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ChartConfiguration, ChartData, ChartDataset } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ZisterneData, ZisterneHistoryPoint } from '../version.service';
import { updatedAgo as fmtAgo } from '../shared/format.utils';
import { CHART_RANGES, CHART_RANGE_MS, ChartRange, chartPalette, coveringRange, dragZoom, timeAxisTick, relativeDayTick, alignedTimeTicks, uiLocale } from '../shared/chart.utils';
import { DeepLinkService } from '../shared/deep-link.service';

const DAY_MS = 24 * 3600 * 1000;
/** Phones (< 576 px): the popup chart gets smaller ticks and no axis title. */
const isPhone = () => window.innerWidth < 576;

@Component({
  selector: 'app-zisterne',
  templateUrl: './zisterne.component.html',
  styles: [`
    .zis-chart { position: relative; aspect-ratio: 16 / 9; }
    @media (max-width: 575.98px) {
      /* popup on a phone: the chart fills the sheet's free height (styles.css dash-sheet-fill / -item) */
      .zis-sheet { height: 100%; }
      .zis-chart { aspect-ratio: auto; }
    }
  `],
})
export class ZisterneComponent implements OnInit, OnChanges, OnDestroy {
  @Input() zisterne: ZisterneData | null = null;
  @Input() zisterneHistory: ZisterneHistoryPoint[] = [];
  @Input() zisterneFlash = false;
  @Input() liveDotOn = true;
  @Input() zisterneUpdatedAt: number | null = null;
  @Input() nowTick = Date.now();
  @Input() darkMode = false;

  /** Deep-link view of the level popup — also the id of its chart (range / zoom keys unprefixed). */
  readonly levelLinkId = 'zisterne/level';

  levelChartData: ChartData<'line'> = { datasets: [] };
  levelChartOptions: ChartConfiguration<'line'>['options'] = {};
  levelExpandedData: ChartData<'line'> = { datasets: [] };
  levelExpandedOptions: ChartConfiguration<'line'>['options'] = {};

  // Expanded-chart range. 'week' is the live 7-day history already in memory;
  // day/month/year fetch the downsampled long-term archive from /api/history.
  range: ChartRange = 'week';
  rangeLoading = false;
  rangeHistory: ZisterneHistoryPoint[] = [];
  /** Range buttons; labels are i18n keys (RANGE_DAY …). */
  readonly chartRanges = CHART_RANGES;
  /** Bumped per range load: a response for an older request is dropped. */
  private rangeSeq = 0;
  private linkSub?: Subscription;
  private langSub?: Subscription;

  constructor(private readonly link: DeepLinkService, private readonly zone: NgZone,
              private readonly translate: TranslateService) {}

  get displayHistory(): ZisterneHistoryPoint[] {
    return this.range === 'week' ? this.zisterneHistory : this.rangeHistory;
  }

  /** The popup's chart shows a zoom window (it is in the link while the popup is open). */
  get zoomed(): boolean { return !!this.link.zoom(this.levelLinkId); }

  updatedAgo(ts: number | null): string { return fmtAgo(ts, this.nowTick); }

  ngOnInit(): void {
    this.applyLink();
    this.linkSub = this.link.external$.subscribe(() => this.applyLink());
    // the series label, axis title, weekday ticks and tooltip dates follow a language switch
    this.langSub = this.translate.onLangChange.subscribe(() => { this.buildSmall(); this.buildExpanded(); });
  }

  ngOnDestroy(): void {
    this.linkSub?.unsubscribe();
    this.langSub?.unsubscribe();
  }

  /** The popup's range from the link, widened so a linked zoom window lies inside the loaded data; the
   *  widened range is written back (buttons, URL and a later Reset agree). A window older than the
   *  longest range would show nothing: it is dropped. (The popup itself follows the link via its linkId.) */
  private applyLink(): void {
    const id = this.levelLinkId;
    const linked = this.link.range(id) ?? 'week';
    const zoom = this.link.zoom(id);
    let range = linked;
    if (zoom && zoom.to <= Date.now() - CHART_RANGE_MS.year) {
      this.link.setZoom(id, null);
    } else {
      range = coveringRange(linked, zoom);
      if (range !== linked) {
        this.link.setRange(id, range, 'week');   // drops the window …
        this.link.setZoom(id, zoom);             // … which still applies
      }
    }
    if (range !== this.range) this.loadRange(range);
  }

  /** Range button: shows the whole window of `range` (drops a zoom). */
  setRange(range: ChartRange): void {
    this.link.setRange(this.levelLinkId, range, 'week');
    this.link.setZoom(this.levelLinkId, null);
    this.loadRange(range);
  }

  /** Popup closed: its range and zoom left the link with the view, so the next open starts at 'week'. */
  onLevelTapped(open: boolean): void {
    if (!open && this.range !== 'week') this.loadRange('week');
  }

  resetZoom(chart: BaseChartDirective): void {
    // chartjs-plugin-zoom calls onZoomComplete → the link's zoom window is dropped
    (chart?.chart as any)?.resetZoom?.();
  }

  private loadRange(range: ChartRange): void {
    this.range = range;
    const seq = ++this.rangeSeq;
    if (range === 'week') { this.rangeLoading = false; this.buildExpanded(); return; }
    const to   = Date.now();
    const from = to - CHART_RANGE_MS[range];
    this.rangeLoading = true;
    fetch(`/api/history?series=zisterne&from=${from}&to=${to}&buckets=600`)
      .then(r => r.json())
      .then((pts: any[]) => {
        if (seq !== this.rangeSeq) return;
        this.rangeHistory = pts ?? []; this.rangeLoading = false; this.buildExpanded();
      })
      .catch(() => { if (seq === this.rangeSeq) this.rangeLoading = false; });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Skip rebuilds triggered solely by the nowTick/liveDotOn ticks.
    if (!changes['zisterneHistory'] && !changes['darkMode']) return;
    this.buildSmall();
    this.buildExpanded();
  }

  private buildSmall(): void {
    if (this.zisterneHistory.length < 2) return;
    this.levelChartData    = this.levelData(this.zisterneHistory);
    this.levelChartOptions = this.smallOptions();
  }

  private buildExpanded(): void {
    const pts = this.displayHistory;
    this.levelExpandedData    = pts.length ? this.levelData(pts) : { datasets: [] };
    // new options also when empty: the axis spans the selected range (and drops an unlinked zoom)
    this.levelExpandedOptions = this.expandedOptions();
  }

  private levelData(pts: ZisterneHistoryPoint[]): ChartData<'line'> {
    return {
      datasets: [{
        label: this.translate.instant('ZISTERNE_FUELLSTAND'),
        linkKey: 'level',
        data: pts.map(p => ({ x: p.ts, y: p.levelPct })),
        borderColor: '#0d6efd',
        backgroundColor: 'rgba(13,110,253,0.15)',
        // hover point in the line's colour, not ng2-charts' default palette
        pointBackgroundColor: '#0d6efd',
        pointBorderColor: '#0d6efd',
        fill: true,
      } as ChartDataset<'line'>],
    };
  }

  /** Small card chart: fixed 7-day window with weekday ticks (at each local midnight: the day it starts;
   *  as many as fit — the card is full width on a phone). */
  private smallOptions(): ChartConfiguration<'line'>['options'] {
    const { textColor, gridColor } = chartPalette(this.darkMode);
    return {
      ...this.commonOptions(false),
      scales: {
        x: {
          display: true, type: 'linear',
          min: Date.now() - 7 * 24 * 3600 * 1000, max: Date.now(),
          afterBuildTicks: (scale: any) => {
            const ticks: { value: number }[] = [];
            const d = new Date(scale.min);
            d.setHours(24, 0, 0, 0);
            for (; d.getTime() <= scale.max; d.setDate(d.getDate() + 1)) ticks.push({ value: d.getTime() });
            if (ticks.length >= 2) scale.ticks = ticks;
          },
          ticks: {
            color: textColor, maxTicksLimit: 7, font: { size: 10 }, maxRotation: 0,
            callback: (val) => new Date(val as number).toLocaleDateString(uiLocale(), { weekday: 'short' }),
          },
          grid: { display: false, color: gridColor },
        },
        y: this.yScale(false),
      },
    };
  }

  /** Expanded chart: axis spans the full selected range window (now-windowMs … now). Zoomed in (a drag /
   *  pinch or a linked window), ticks on whole hours / days of the visible span instead. */
  private expandedOptions(): ChartConfiguration<'line'>['options'] {
    const { textColor, gridColor } = chartPalette(this.darkMode);
    const phone = isPhone();
    const xMax = Date.now();
    const xMin = xMax - CHART_RANGE_MS[this.range];
    const windowTick = this.range === 'day' ? relativeDayTick : timeAxisTick(xMin, xMax);
    const zoomedIn = (scale: any): boolean => scale.max - scale.min < (xMax - xMin) * 0.98;
    const pad = (n: number) => String(n).padStart(2, '0');
    return {
      ...this.commonOptions(true),
      scales: {
        x: {
          display: true, type: 'linear', min: xMin, max: xMax,
          afterBuildTicks: (scale: any) => {
            const aligned = zoomedIn(scale) ? alignedTimeTicks(scale.min, scale.max) : [];
            if (aligned.length >= 2) scale.ticks = aligned.map(value => ({ value }));
          },
          ticks: { color: textColor, maxTicksLimit: phone ? 5 : 8, font: { size: phone ? 10 : 11 }, maxRotation: 0,
                   callback: function (this: any, val: number | string) {
                     if (!zoomedIn(this)) return windowTick(val);
                     if (this.max - this.min > 2 * DAY_MS) return timeAxisTick(this.min, this.max)(val);
                     const d = new Date(val as number);
                     return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
                   } },
          grid: { display: true, color: gridColor },
        },
        y: this.yScale(true),
      },
    };
  }

  private yScale(expanded: boolean): any {
    const { textColor, gridColor } = chartPalette(this.darkMode);
    // phones: no axis title (the ticks carry the %), 10 px ticks
    const big = expanded && !isPhone();
    return {
      // fixed 0–100 % (a fill gauge): the min-span plugin leaves it alone; resolution = the tooltip's 0.1 %
      display: true, min: 0, max: 100, resolution: 0.1,
      ticks: { color: textColor, maxTicksLimit: expanded ? 6 : 4, font: { size: big ? 11 : 10 },
               callback: (val: any) => `${val}%` },
      grid: { color: gridColor, display: expanded },
      title: { display: big, text: this.translate.instant('ZISTERNE_FUELLSTAND_PCT'), color: textColor, font: { size: 11 } },
    };
  }

  private commonOptions(expanded: boolean): ChartConfiguration<'line'>['options'] {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 4, right: 6, bottom: 0, left: 0 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const ts = items[0]?.parsed.x;
              if (ts == null) return '';
              return new Date(ts).toLocaleString(uiLocale(), {
                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
              });
            },
            label: (item) => ` ${(item.parsed.y ?? 0).toFixed(1)} %`,
          },
        },
        // The popup's chart: zoom window in the deep link. Chart.js calls onZoomComplete outside
        // Angular — re-enter the zone so the Reset button follows. (deepLink is not in Chart.js' typings.)
        ...(expanded ? {
          zoom: dragZoom(() => this.zone.run(() => {})),
          deepLink: { id: this.levelLinkId },
        } as object : {}),
      },
      elements: {
        point: { radius: 0, hoverRadius: 4 },
        line: { borderWidth: expanded ? 2 : 1.5, tension: 0.4 },
      },
    };
  }
}
